import React, { useState, useRef } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import emailjs from "emailjs-com";
import "./Contact.css";

const Contact = () => {
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [validated, setValidated] = useState(false);
  const formRef = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);

    // EmailJS configuration
    const serviceId = "service_d7zhpi2"; // Replace with your EmailJS service ID
    const templateId = "template_poudi7e"; // Replace with your EmailJS template ID
    const userId = "GdOVVzg4Brs7Llvan"; // Replace with your EmailJS user ID

    // Send the email
    emailjs.sendForm(serviceId, templateId, formRef.current, userId).then(
      (result) => {
        setFormStatus({
          submitted: true,
          success: true,
          message:
            "Your message has been sent successfully! We will get back to you soon.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setValidated(false);
      },
      (error) => {
        setFormStatus({
          submitted: true,
          success: false,
          message:
            "There was an error sending your message. Please try again later.",
        });
      }
    );
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="contact-page">
      <div className="contact-header">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8} md={10}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <h1 className="page-title">Contact Us</h1>
                <p className="lead">
                  Have questions or ready to start your project? Get in touch
                  with our team and we'll get back to you shortly.
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="contact-section">
        <Container>
          <Row>
            <Col lg={5} md={12} className="mb-5 mb-lg-0">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                className="contact-info"
              >
                <h3>Get In Touch</h3>
                <p>
                  Feel free to reach out to us using the contact information
                  below or by filling out the form. We're here to help with any
                  questions you may have.
                </p>

                <div className="contact-details">
                  <div className="contact-item">
                    <div className="contact-icon">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <h5>Our Location</h5>
                      <p>
                        IT park
                        <br />
                        Indore, Madhya Pradesh
                      </p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <FaPhone />
                    </div>
                    <div>
                      <h5>Phone Number</h5>
                      <p>+91-9109643376</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <FaEnvelope />
                    </div>
                    <div>
                      <h5>Email Address</h5>
                      <p>info@networkBuilder.com</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <FaClock />
                    </div>
                    <div>
                      <h5>Working Hours</h5>
                      <p>
                        Monday - Friday: 9:00 AM - 6:00 PM
                        <br />
                        Saturday: 10:00 AM - 4:00 PM
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </Col>

            <Col lg={7} md={12}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                className="contact-form-container"
              >
                <h3>Send Us a Message</h3>

                {formStatus.submitted && (
                  <Alert variant={formStatus.success ? "success" : "danger"}>
                    {formStatus.message}
                  </Alert>
                )}

                <Form
                  ref={formRef}
                  noValidate
                  validated={validated}
                  onSubmit={handleSubmit}
                >
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label>Your Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide your name.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid email address.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="What is this regarding?"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a subject.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label>Your Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="How can we help you?"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter your message.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button type="submit" className="btn-gradient btn-lg w-100">
                    Send Message
                  </Button>
                </Form>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="map-section">
        <div className="map-container">
          {/* <iframe
            title="Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d100939.98555098464!2d-122.50764017911858!3d37.75781499671794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80859a6d00690021%3A0x4a501367f076adff!2sSan%20Francisco%2C%20CA%2C%20USA!5e0!3m2!1sen!2sin!4v1636534867663!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe> */}
          <iframe
          title="Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7113.041665797345!2d75.86784022432302!3d22.685725299999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd11cbaf30c5%3A0xeddd100ed6a7f182!2sIT%20park%20indore!5e1!3m2!1sen!2sin!4v1742989409667!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{border:0}}
            allowfullscreen=""
            loading="lazy"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;
